import React, { useState } from "react"
import { FileCheck, Loader2, Share2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import { useToast } from "src/components/ui/toast"
import { ShareDocumentDrawer } from "src/features/documents/components/ShareDocumentDrawer"
import { useGenerateBookingConfirmation } from "../hooks/useBookings"

// (Re)generates the branded Booking Confirmation / Allotment Letter PDF from the
// current org template + this booking's data, opens it inline in a new tab, and
// exposes a Share action. Mirrors QuotationButton. The letter is also generated
// automatically on booking creation; this lets an admin refresh it after
// editing the template under Configurations → Document Templates.
export default function BookingConfirmationButton({
  bookingId,
  variant = "outline",
  size = "sm",
  label = "Booking letter",
  className = "gap-1.5",
  onGenerated,
}) {
  const { mutate, isPending } = useGenerateBookingConfirmation()
  const { toast } = useToast()
  const [generatedDoc, setGeneratedDoc] = useState(null)
  const [shareOpen, setShareOpen] = useState(false)

  async function handleGenerate() {
    try {
      const doc = await mutate(bookingId)
      setGeneratedDoc(doc || null)
      toast({ title: "Booking letter ready", description: "The confirmation PDF has been generated." })
      onGenerated?.(doc)
      if (doc?.url) window.open(doc.url, "_blank")
    } catch (errors) {
      toast({
        variant: "destructive",
        title: "Error",
        description: Array.isArray(errors) ? errors[0] : "Failed to generate booking letter",
      })
    }
  }

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={handleGenerate} disabled={isPending}>
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck className="h-4 w-4" />}
        {label}
      </Button>
      {generatedDoc && (
        <Button variant="ghost" size={size} className="gap-1.5" onClick={() => setShareOpen(true)} title="Share booking letter">
          <Share2 className="h-4 w-4" /> Share
        </Button>
      )}
      <ShareDocumentDrawer document={generatedDoc} open={shareOpen} onOpenChange={setShareOpen} />
    </>
  )
}
